// filepath: src/services/videoUrl/videoUrlSweep.ts
/**
 * Post-login sweep: walk every collection that contains a `videoUrl` field
 * across every locale and (where applicable) every project, sanitize each
 * URL via `sanitizeVideoUrl`, and PUT back the cleaned record when changed.
 *
 * Designed to run once per login (fire-and-forget) — failures are logged
 * but do not block the user's login.
 */

import { sanitizeVideoUrl } from './videoUrlService';
import {
  fetchShowreel,
  updateShowreelItem,
} from '../api/showreelApi';
import {
  fetchReferences,
  updateReference,
} from '../api/referencesApi';
import {
  fetchHeroItems,
  updateHeroItem,
} from '../api/boltJr/heroApi';
import {
  fetchBoltVideos,
  updateBoltVideo,
} from '../api/boltJr/videosApi';

export interface SweepReport {
  totalScanned: number;
  totalUpdated: number;
  failures: string[];
  details: Array<{
    collection: string;
    locale: string;
    itemId: string;
    removedParams: string[];
  }>;
}

const LOCALES = ['en', 'hu'] as const;

type ItemWithVideoUrl = { id: string; videoUrl?: string | null };

async function sweepCollection<T extends ItemWithVideoUrl>(
  collection: string,
  locale: string,
  fetcher: (locale?: string) => Promise<T[]>,
  updater: (id: string, data: T, locale?: string) => Promise<T>,
  report: SweepReport
): Promise<void> {
  let items: T[];
  try {
    items = await fetcher(locale);
  } catch (err) {
    report.failures.push(
      `[${collection}/${locale}] fetch failed: ${err instanceof Error ? err.message : String(err)}`
    );
    return;
  }

  for (const item of items) {
    report.totalScanned += 1;
    if (!item.videoUrl) continue;
    const result = sanitizeVideoUrl(item.videoUrl);
    if (!result.changed) continue;

    try {
      await updater(item.id, { ...item, videoUrl: result.sanitized }, locale);
      report.totalUpdated += 1;
      report.details.push({
        collection,
        locale,
        itemId: item.id,
        removedParams: result.removedParams,
      });
    } catch (err) {
      report.failures.push(
        `[${collection}/${locale}/${item.id}] update failed: ${err instanceof Error ? err.message : String(err)}`
      );
    }
  }
}

/**
 * Run the full sweep across all known collections + locales.
 * Returns a summary report. Never throws.
 */
export async function runVideoUrlSweep(): Promise<SweepReport> {
  const report: SweepReport = {
    totalScanned: 0,
    totalUpdated: 0,
    failures: [],
    details: [],
  };

  for (const locale of LOCALES) {
    // Admiral
    await sweepCollection('showreel', locale, fetchShowreel, updateShowreelItem, report);
    await sweepCollection('references', locale, fetchReferences, updateReference, report);
    // Bolt Jr
    await sweepCollection('bolt-jr/hero', locale, fetchHeroItems, updateHeroItem, report);
    await sweepCollection('bolt-jr/videos', locale, fetchBoltVideos, updateBoltVideo, report);
  }

  return report;
}
